import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/helpers/classnames';
import {
  IconCheckCircle,
  IconInfoCircle,
  IconWarningTriangle,
  IconXMarkCircle,
} from '@/icons';

const alertVariants = cva('flex gap-3 rounded-md border p-4 text-base', {
  variants: {
    severity: {
      error: 'border-red-500 bg-red-500/10 text-red-800',
      warning: 'border-yellow-500 bg-yellow-500/20 text-yellow-800',
      info: 'border-blue-500 bg-blue-500/10 text-blue-800',
      success: 'border-green-500 bg-green-500/10 text-green-800',
    },
  },
  defaultVariants: { severity: 'info' },
});

const icons = {
  error: IconXMarkCircle,
  warning: IconWarningTriangle,
  info: IconInfoCircle,
  success: IconCheckCircle,
};

interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof alertVariants> {
  title?: React.ReactNode;
}

const Alert: React.FC<AlertProps> = ({
  severity = 'info',
  title,
  className,
  children,
  ...props
}) => {
  const Icon = icons[severity ?? 'info'];
  return (
    <div
      role="alert"
      className={cn(alertVariants({ severity }), className)}
      {...props}
    >
      <Icon className="mt-0.5 size-5 shrink-0" />
      <div className="min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  );
};

export default Alert;
